import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Demo users for SecureBank team
  const demoUsers = {
    'tech.manager': {
      id: 'user-tech-manager',
      username: 'tech.manager',
      email: 'tech.manager@globalsoc.io',
      role: 'admin',
      fullName: 'Technical Manager',
      avatar: null
    },
    'cloud.engineer': {
      id: 'user-cloud-engineer',
      username: 'cloud.engineer',
      email: 'cloud.engineer@globalsoc.io',
      role: 'engineer',
      fullName: 'Cloud Engineer',
      avatar: null
    },
    'team.lead': {
      id: 'user-team-lead',
      username: 'team.lead',
      email: 'team.lead@globalsoc.io',
      role: 'engineer_lead',
      fullName: 'Team Lead',
      avatar: null
    },
    'developer.001': {
      id: 'user-developer',
      username: 'developer.001',
      email: 'developer.001@globalsoc.io',
      role: 'developer',
      fullName: 'Developer',
      avatar: null
    },
    'client.contact': {
      id: 'user-client',
      username: 'client.contact',
      email: 'client.contact@globalsoc.io',
      role: 'client',
      fullName: 'Client Contact',
      avatar: null
    }
  };

  // Demo passwords - In production, these would be hashed and stored securely
  const demoPasswordHashes = {
    'tech.manager': 'admin123',    // Technical Manager
    'cloud.engineer': 'cloud123', // Cloud Engineer  
    'team.lead': 'lead123',       // Engineer Lead
    'developer.001': 'dev123',    // Developer
    'client.contact': 'client123' // Client Contact
  };

  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('blueteam_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('blueteam_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      // Demo authentication
      if (demoUsers[username] && demoPasswordHashes[username] === password) {
        const userData = demoUsers[username];
        setUser(userData);
        localStorage.setItem('blueteam_user', JSON.stringify(userData));
        return { success: true, user: userData };
      } else {
        throw new Error('Invalid credentials. Please check your username and password.');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('blueteam_user');
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('blueteam_user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}